import React, { FunctionComponent, useState } from "react";
import { Dialog, IconButton } from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-light-svg-icons";
import { useSelector } from "react-redux";
import clsx from "clsx";
import { RootState } from "store";
import { Button, Spinner } from "components/UI";
import { SystemEmailTemplate } from "model/SystemEmailTemplate";

type PreviewDevice = "desktop" | "mobile";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Fires when the user clicks "Edit" in the preview header — parent owns the
   *  transition to the editor route/mode. */
  onEdit: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      backgroundColor: "#F4F4F4"
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "16px",
      padding: "16px 24px",
      backgroundColor: "#fff",
      borderBottom: "1px solid #E5E5E5",
      flexShrink: 0
    },
    nameWrap: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      flex: "1 1 auto",
      minWidth: 0
    },
    name: {
      fontFamily: "Poppins",
      fontWeight: 600,
      fontSize: "18px",
      color: "#222222",
      margin: 0,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      minWidth: 0
    },
    badge: {
      display: "inline-block",
      padding: "3px 10px",
      borderRadius: "999px",
      fontFamily: "Poppins",
      fontWeight: 500,
      fontSize: "12px",
      lineHeight: 1.2,
      flexShrink: 0
    },
    badgePublished: {
      backgroundColor: "#E4F3EA",
      color: "#2E7D5B"
    },
    badgeDraft: {
      backgroundColor: "#EEEEEE",
      color: "#666666"
    },
    deviceToggle: {
      display: "inline-flex",
      borderRadius: "999px",
      padding: "4px",
      backgroundColor: "#EEEEEE",
      flexShrink: 0
    },
    deviceButton: {
      border: 0,
      outline: "none",
      padding: "6px 14px",
      backgroundColor: "transparent",
      borderRadius: "999px",
      cursor: "pointer",
      fontFamily: "Poppins",
      fontSize: "13px",
      color: "#555",
      transition: "background-color 0.15s ease, color 0.15s ease"
    },
    deviceButtonActive: {
      backgroundColor: "#222222",
      color: "#fff"
    },
    rightActions: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      flexShrink: 0
    },
    closeButton: {
      padding: "8px",
      color: "#555"
    },
    body: {
      flex: "1 1 auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "40px 24px",
      overflow: "auto"
    },
    previewWindow: {
      display: "flex",
      flexDirection: "column",
      backgroundColor: "#fff",
      borderRadius: "8px",
      boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.15)",
      overflow: "hidden",
      maxHeight: "100%"
    },
    previewWindow_desktop: {
      width: "min(960px, 100%)",
      height: "min(720px, calc(100vh - 160px))"
    },
    previewWindow_mobile: {
      width: "min(390px, 100%)",
      height: "min(720px, calc(100vh - 160px))"
    },
    browserChrome: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      padding: "10px 14px",
      borderBottom: "1px solid #EEE",
      backgroundColor: "#F8F8F8",
      flexShrink: 0
    },
    dot: {
      width: "10px",
      height: "10px",
      borderRadius: "50%"
    },
    dotRed:    { backgroundColor: "#FF5F57" },
    dotYellow: { backgroundColor: "#FEBC2E" },
    dotGreen:  { backgroundColor: "#28C840" },
    iframeWrapper: {
      flex: "1 1 auto",
      overflow: "hidden",
      backgroundColor: "#fff"
    },
    iframe: {
      width: "100%",
      height: "100%",
      border: 0
    },
    placeholder: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      height: "100%",
      color: "#777",
      fontFamily: "Poppins",
      fontSize: "14px"
    },
    spinnerWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      height: "100%"
    }
  })
);

/**
 * Full-window template preview, framed as a faux browser window so super
 * admins read it the way a recipient would (Unlayer / Mailchimp pattern,
 * ported from the marketplace's `EmailTemplatePreviewModal`).
 *
 * Data flow: the parent (`EmailTemplates`) dispatches `fetchTemplateThunk`
 * when a row is selected — this modal just consumes `currentTemplate` from
 * the slice. While the fetch is in-flight, a spinner replaces the iframe.
 *
 * Unlike the marketplace's view-only modal, the admin variant exposes an
 * "Edit" action in the header (per user's flow: preview → Edit → editor).
 */
export const TemplatePreviewModal: FunctionComponent<Props> = ({ open, onClose, onEdit }) => {
  const classes = useStyles();
  const [device, setDevice] = useState<PreviewDevice>("desktop");

  const template = useSelector<RootState, SystemEmailTemplate | undefined>(
    s => s.systemEmailTemplates.currentTemplate
  );
  const loading = useSelector<RootState, boolean>(s => s.systemEmailTemplates.loadingTemplate);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ className: classes.paper }}
    >
      <div className={classes.header}>
        <div className={classes.nameWrap}>
          <h2 className={classes.name}>{template?.name ?? "Loading…"}</h2>
          {template && (
            <span
              className={clsx(
                classes.badge,
                template.published ? classes.badgePublished : classes.badgeDraft
              )}
            >
              {template.published ? "Published" : "Draft"}
            </span>
          )}
        </div>

        <div className={classes.deviceToggle}>
          <button
            type="button"
            className={clsx(classes.deviceButton, { [classes.deviceButtonActive]: device === "desktop" })}
            onClick={() => setDevice("desktop")}
          >
            Desktop
          </button>
          <button
            type="button"
            className={clsx(classes.deviceButton, { [classes.deviceButtonActive]: device === "mobile" })}
            onClick={() => setDevice("mobile")}
          >
            Mobile
          </button>
        </div>

        <div className={classes.rightActions}>
          <Button
            label="Edit"
            onClick={onEdit}
            disabled={loading || !template}
          />
          <IconButton
            aria-label="Close preview"
            className={classes.closeButton}
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faTimes} />
          </IconButton>
        </div>
      </div>

      <div className={classes.body}>
        <div
          className={clsx(
            classes.previewWindow,
            device === "desktop" ? classes.previewWindow_desktop : classes.previewWindow_mobile
          )}
        >
          <div className={classes.browserChrome} aria-hidden="true">
            <span className={clsx(classes.dot, classes.dotRed)} />
            <span className={clsx(classes.dot, classes.dotYellow)} />
            <span className={clsx(classes.dot, classes.dotGreen)} />
          </div>
          <div className={classes.iframeWrapper}>
            {loading || !template ? (
              <div className={classes.spinnerWrap}>
                <Spinner />
              </div>
            ) : template.html ? (
              <iframe
                className={classes.iframe}
                title={template.name}
                srcDoc={template.html}
                sandbox=""
              />
            ) : (
              <div className={classes.placeholder}>This template has no content yet.</div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default TemplatePreviewModal;
